import { PropsWithChildren, useState, useCallback, useMemo, useEffect, useContext, createContext } from "react";
import { useQueryClient } from "react-query";
import { isAuthenticated, setAccessToken } from "../service/axios";
import { ISubscription, IUser } from "../service/types";
import { useProfileSubscriptions, UserHook, useUpdateProfileMutation } from "../service/user/hooks";
import { useTheme } from "./ThemeProvider";
import { logoutTiledeskWidget } from "../service/tiledesk/useTiledeskWidget";
import { saveFcmToken } from "../service/user/api";
import { spawnAppEvent } from "../service/utils";

interface IProfileContext {
  profile: IUser | null;
  subscriptions: ISubscription[];
  isLoading: boolean;
  updateProfile: (data: Record<string, string | boolean>) => void;
  updateProfileImage: (file: File) => void;
  logout: () => void;
}

const defaultProfileContextData: IProfileContext = {
  profile: null,
  subscriptions: [],
  updateProfile: () => {},
  updateProfileImage: () => {},
  logout: () => {},
  isLoading: false,
};

const ProfileContext = createContext(defaultProfileContextData);

export function ProfileProvider({ children }: PropsWithChildren) {
  const [data, setData] = useState(defaultProfileContextData);
  const { id, fetchBenefits } = useTheme();
  const queryClient = useQueryClient();
  const { data: subscriptions = [] } = useProfileSubscriptions();
  const onUpdateSuccess = useCallback(
    (profile: IUser | null) => {
      if (!profile) return;

      localStorage.setItem('profile', JSON.stringify(profile));
      if (id) {
        fetchBenefits(id);
      }
      queryClient.refetchQueries(UserHook.ProfileSubscriptions);
      setData((data) => ({ ...data, profile }));
    },
    [id, fetchBenefits, queryClient]
  );
  const updateProfileMutation = useUpdateProfileMutation({
    onSuccess: onUpdateSuccess,
  });
  const isLoading = useMemo(() => updateProfileMutation.isLoading, [updateProfileMutation]);
  const updateProfile = useCallback(
    (data: Record<string, string | boolean>) => {
      setData((data) => ({
        ...data,
      }));
      updateProfileMutation.mutate(data);
    },
    [updateProfileMutation]
  );

  const updateProfileImage = useCallback(
    (file: File) => {
      const data: any = new FormData();
      data.append('image', file);
      updateProfileMutation.mutate(data);
    },
    [updateProfileMutation]
  );

  // App whitelabel (WebView) envia "fcmToken:::<token>"; salvamos na API para o usuário logado
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  useEffect(() => {
    if (!window.ReactNativeWebView) return;
    const onMessage = (event: Event) => {
      const raw = (event as MessageEvent).data;
      if (typeof raw !== 'string' || !raw.startsWith('fcmToken:::')) return;
      const token = raw.slice('fcmToken:::'.length);
      if (token) setFcmToken(token);
    };
    // Android dispara em document, iOS em window
    window.addEventListener('message', onMessage);
    document.addEventListener('message', onMessage);
    spawnAppEvent('getFcmToken');
    return () => {
      window.removeEventListener('message', onMessage);
      document.removeEventListener('message', onMessage);
    };
  }, []);

  const profileId = data.profile?.id;
  useEffect(() => {
    if (!fcmToken || !profileId || !isAuthenticated()) return;
    const sentKey = `${profileId}:${fcmToken}`;
    if (localStorage.getItem('fcmTokenSent') === sentKey) return;
    const platform = /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'ios' : 'android';
    saveFcmToken(fcmToken, platform)
      .then(() => localStorage.setItem('fcmTokenSent', sentKey))
      .catch(() => {});
  }, [fcmToken, profileId]);

  useEffect(() => {
    if (!isAuthenticated()) return;
    const savedProfile = JSON.parse(localStorage.getItem('profile') || 'null');
    if (savedProfile) setData((data) => ({ ...data, profile: savedProfile }));
    updateProfileMutation.mutate({});
  }, []);

  return (
    <ProfileContext.Provider
      value={{
        ...data,
        subscriptions,
        isLoading: isLoading,
        updateProfile,
        updateProfileImage,
        logout() {
          const existingUrlData = localStorage.getItem('urlData');

          // encerra a sessão do chat antes do reload; SDK de terceiro, nunca bloqueia o logout
          logoutTiledeskWidget();

          setAccessToken(null, null);
          localStorage.clear();
          localStorage.setItem('geolocation_permission', 'true');
          if (existingUrlData) localStorage.setItem('urlData', existingUrlData);

          window.location.reload();
        },
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  return useContext(ProfileContext);
}
